import {
  createContext, useCallback, useContext, useMemo, useRef, useState, ReactNode,
} from "react";

export type Period = "12m" | "6m" | "3m" | "1m";

export type FilterValues = Record<string, string[]>;

export interface DashboardState {
  page: string;
  period: Period;
  store?: string;
  start_month?: string;
  end_month?: string;
  filters: FilterValues;
  theme: "light" | "dark";
  selections: string[];
  focus?: Record<string, any>;
}

export interface DashboardPatch {
  page?: string;
  period?: Period;
  store?: string;
  start_month?: string;
  end_month?: string;
  filters?: FilterValues;
  theme?: "light" | "dark";
  selections?: string[];
  focus?: Record<string, any>;
}

interface Ctx {
  filters: DashboardState;
  filterKeys: string[];
  ready: number;
  setFilterKeys: (keys: string[]) => void;
  setPeriod: (p: Period) => void;
  setPage: (p: string) => void;
  setStore: (s: string | undefined) => void;
  setFilterValue: (id: string, values: string[]) => void;
  clearFilters: () => void;
  applyPatch: (patch: DashboardPatch) => void;
  markReady: () => void;
  reset: () => void;
}

const DEFAULT: DashboardState = {
  page: "resumo",
  period: "12m",
  store: undefined,
  start_month: undefined,
  end_month: undefined,
  filters: {},
  theme: "light",
  selections: [],
  focus: undefined,
};

const C = createContext<Ctx>({
  filters: DEFAULT,
  filterKeys: [],
  ready: 0,
  setFilterKeys: () => {},
  setPeriod: () => {},
  setPage: () => {},
  setStore: () => {},
  setFilterValue: () => {},
  clearFilters: () => {},
  applyPatch: () => {},
  markReady: () => {},
  reset: () => {},
});

function normalizeFilters(keys: string[], candidate: FilterValues): FilterValues {
  if (!keys.length) return { ...candidate };
  const out: FilterValues = {};
  for (const k of keys) {
    const v = candidate[k];
    if (Array.isArray(v) && v.length > 0) out[k] = v.slice();
  }
  return out;
}
function hasActiveFilters(f: FilterValues): boolean {
  return Object.values(f).some((v) => Array.isArray(v) && v.length > 0);
}

export function DashboardFiltersProvider({ children }: { children: ReactNode }) {
  const [filters, setFilters] = useState<DashboardState>(DEFAULT);
  const [filterKeys, setFilterKeys] = useState<string[]>([]);
  const [ready, setReady] = useState(0);
  const filterKeysRef = useRef<string[]>([]);
  filterKeysRef.current = filterKeys;

  const applyPatch = useCallback((patch: DashboardPatch) => {
    if (!patch || typeof patch !== "object") return;
    setFilters((prev) => {
      const nextFilters = patch.filters
        ? normalizeFilters(filterKeysRef.current, { ...prev.filters, ...patch.filters })
        : prev.filters;
      const clearedAll = patch.filters !== undefined && !hasActiveFilters(nextFilters);
      const next: DashboardState = {
        ...prev,
        ...(patch.page !== undefined ? { page: String(patch.page) } : {}),
        ...(patch.period !== undefined ? { period: patch.period } : {}),
        ...(patch.store !== undefined ? { store: patch.store || undefined } : {}),
        ...(patch.start_month !== undefined ? { start_month: patch.start_month || undefined } : {}),
        ...(patch.end_month !== undefined ? { end_month: patch.end_month || undefined } : {}),
        ...(patch.theme !== undefined ? { theme: patch.theme } : {}),
        filters: nextFilters,
        selections: clearedAll ? [] : (patch.selections ?? prev.selections),
        focus: clearedAll ? undefined : (patch.focus ?? prev.focus),
      };
      return next;
    });
  }, []);

  const value = useMemo<Ctx>(() => ({
    filters,
    filterKeys,
    ready,
    setFilterKeys,
    setPeriod: (p) => setFilters((f) => ({ ...f, period: p })),
    setPage: (p) => setFilters((f) => ({ ...f, page: p })),
    setStore: (s) => setFilters((f) => ({ ...f, store: s })),
    setFilterValue: (id, values) => {
      setFilters((f) => {
        const merged = { ...f.filters, [id]: values.slice() };
        const normalized = normalizeFilters(filterKeysRef.current, merged);
        const clearedAll = !hasActiveFilters(normalized);
        return {
          ...f,
          filters: normalized,
          selections: clearedAll ? [] : f.selections,
          focus: clearedAll ? undefined : f.focus,
        };
      });
    },
    clearFilters: () => setFilters((f) => ({ ...f, filters: {}, selections: [], focus: undefined })),
    applyPatch,
    markReady: () => setReady((r) => r + 1),
    reset: () => setFilters(DEFAULT),
  }), [filters, filterKeys, ready, applyPatch]);

  return <C.Provider value={value}>{children}</C.Provider>;
}

export function useDashboardFilters() {
  return useContext(C);
}
